import React, {useEffect, useId, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {SpinnerButton} from '../../SpinnerButton';
import {InviteClientToManageConnectionsButton} from '../../client/ad_accounts/InviteClientToManageConnectionsButton';
import {AgencyClientPicker, asClientId, useAgencyClients} from '../../connection_links/AgencyClientPicker';
import {MercadoAdsErrorView} from './MercadoAdsErrorView';
import {startMercadoAdsAgencyAuth} from './mercadoAdsApi';
import {ERROR_GENERIC} from './mercadoAds';

/**
 * El paso previo del alta de Mercado Ads: «¿Quién va a dar el acceso?» (contrato §6.1, `copy.md` §2).
 *
 * Casi siempre autoriza el cliente: Mercado Libre deja conectar sólo la cuenta PRINCIPAL del vendedor,
 * y la agencia rara vez la tiene. Por eso «Enviar link al vendedor» va primero y marcado
 * «Recomendado»; conectar con la cuenta principal queda segundo, con la advertencia de los usuarios
 * colaboradores a la vista ANTES de ir. Abajo, en chico, el Portal, para el cliente que ya lo usa: la indicación
 * y el botón de invitarlo. Con un cliente elegido (la ficha) va con ése; sin cliente (Conexiones de la agencia),
 * primero se elige a quién, con el mismo selector de «Enviar link al vendedor» (fede, 21/10).
 *
 * Si no se puede ir a Mercado Libre (el conector apagado, el backend caído), el error queda acá mismo.
 */
export function MercadoAdsChooseStep({onSendLink, agencyClientId = null, start = startMercadoAdsAgencyAuth}) {
    const {t} = useTranslation();
    const pickerId = useId();
    const [going, setGoing] = useState(false);
    const [error, setError] = useState(null);
    const pickClient = !agencyClientId;
    const [clients, reloadClients] = useAgencyClients(pickClient);
    const [pickedClient, setPickedClient] = useState('');
    const inviteClientId = agencyClientId || (pickedClient ? asClientId(pickedClient) : null);
    const alive = useRef(true);
    useEffect(() => () => { alive.current = false; }, []);

    const connectMainUser = () => {
        if (going) return;
        setGoing(true);
        setError(null);
        start().then((result) => {
            if (result && result.ok) return;
            if (!alive.current) return;
            setGoing(false);
            setError((result && result.code) || ERROR_GENERIC);
        });
    };

    return (
        <div className="ma-choose" data-testid="mercado-ads-choose">
            <span className="ma-choose__title" id="ma-choose-title">{t('mercado_ads_link.choose.title')}</span>
            <div className="ma-choose__options" role="group" aria-labelledby="ma-choose-title">
                <button type="button" className="ma-option -recommended" onClick={onSendLink} disabled={going}
                        data-testid="mercado-ads-choose-send-link">
                    <span className="ma-option__head">
                        <span className="ma-option__name">{t('mercado_ads_link.choose.send_link')}</span>
                        <span className="ma-chip">{t('mercado_ads_link.choose.recommended')}</span>
                    </span>
                    <span className="ma-option__help">{t('mercado_ads_link.choose.send_link_help')}</span>
                </button>

                <button type="button" className="ma-option" onClick={connectMainUser} disabled={going}
                        aria-busy={going || undefined} data-testid="mercado-ads-choose-main-user">
                    <span className="ma-option__head">
                        <span className="ma-option__name">{t('mercado_ads_link.choose.main_user')}</span>
                        {going && <SpinnerButton/>}
                    </span>
                    <span className="ma-option__help">{t('mercado_ads_link.choose.main_user_help')}</span>
                    <span className="ma-option__notice">{t('mercado_ads_link.choose.main_user_notice')}</span>
                </button>
            </div>

            {error && <MercadoAdsErrorView code={error}/>}

            <div className={`ma-choose__portal${pickClient ? ' -pick' : ''}`} data-testid="mercado-ads-choose-portal">
                <span className="ma-choose__portal-text">{t('mercado_ads_link.choose.invite_portal')}</span>
                {pickClient && (
                    <AgencyClientPicker id={pickerId} clients={clients} value={pickedClient} onChange={setPickedClient}
                                        onRetry={reloadClients}/>
                )}
                {inviteClientId && <InviteClientToManageConnectionsButton clientId={inviteClientId}/>}
            </div>
            <span className="ma-choose__readonly">{t('mercado_ads_link.choose.read_only')}</span>
        </div>
    );
}

export default MercadoAdsChooseStep;
